import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import { ArrowBack, Send, AttachFile, Close, InsertDriveFile, Image } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import Context from '../../../contexts/Context'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

const isImageFile = (file) => file.type?.startsWith('image/')

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const TicketCreate = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useContext(Context)
  const fileInputRef = useRef(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState([])
  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM',
    category: '',
  })

  useEffect(() => {
    api.get('tickets/categories/')
      .then(res => setCategories(res.data.results || res.data))
      .catch(err => console.error(err))
  }, [])

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    const validFiles = []

    selectedFiles.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        enqueueSnackbar(`"${file.name}" excede o limite de 10MB.`, { variant: 'warning' })
      } else {
        validFiles.push(file)
      }
    })

    setFiles(prev => [...prev, ...validFiles])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const uploadAttachments = async (ticketId) => {
    for (const file of files) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('ticket', ticketId)
      try {
        await api.post(`tickets/${ticketId}/attachments/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      } catch (err) {
        console.error('Erro ao enviar anexo:', file.name, err)
        enqueueSnackbar(`Erro ao enviar "${file.name}".`, { variant: 'warning' })
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.title || !form.description || !form.category) {
      enqueueSnackbar('Preencha todos os campos obrigatórios.', { variant: 'warning' })
      return
    }

    setLoading(true)
    try {
      const res = await api.post('tickets/', {
        ...form,
        condo: getCondoId(),
      })
      const ticketId = res.data.id

      if (files.length > 0) {
        await uploadAttachments(ticketId)
      }

      enqueueSnackbar('Ticket criado com sucesso!', { variant: 'success' })
      navigate(`/suporte/${ticketId}`)
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Erro ao criar ticket. Tente novamente.', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/suporte')}
          sx={{ color: '#64748B' }}
        >
          Voltar
        </Button>
      </Box>

      <Box maxWidth={720} mx="auto">
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.5 }}>
          Abrir Novo Ticket
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
          Descreva seu problema ou solicitação e nossa equipe responderá o mais breve possível.
        </Typography>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2.5}>
              <TextField
                label="Título"
                placeholder="Resumo do seu problema ou solicitação"
                fullWidth
                required
                value={form.title}
                onChange={handleChange('title')}
              />

              <Box display="flex" gap={2}>
                <FormControl fullWidth required>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    value={form.category}
                    label="Categoria"
                    onChange={handleChange('category')}
                  >
                    {categories.map(cat => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Prioridade</InputLabel>
                  <Select
                    value={form.priority}
                    label="Prioridade"
                    onChange={handleChange('priority')}
                  >
                    <MenuItem value="LOW">Baixa</MenuItem>
                    <MenuItem value="MEDIUM">Média</MenuItem>
                    <MenuItem value="HIGH">Alta</MenuItem>
                    <MenuItem value="URGENT">Urgente</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Descrição"
                placeholder="Descreva detalhadamente o que está acontecendo, passos para reproduzir o problema, etc."
                fullWidth
                required
                multiline
                rows={6}
                value={form.description}
                onChange={handleChange('description')}
              />

              {/* Área de upload de arquivos */}
              <Box>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar"
                />
                <Button
                  variant="outlined"
                  startIcon={<AttachFile />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ color: '#64748B', borderColor: '#CBD5E1', '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' } }}
                >
                  Anexar arquivos
                </Button>
                <Typography variant="caption" sx={{ color: '#94A3B8', ml: 1.5 }}>
                  Máx. 10MB por arquivo (imagens, PDF, documentos)
                </Typography>

                {files.length > 0 && (
                  <Box display="flex" flexDirection="column" gap={1} mt={1.5}>
                    {files.map((file, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: '#F8FAFC',
                          border: '1px solid #E2E8F0',
                        }}
                      >
                        {isImageFile(file) ? (
                          <Box
                            component="img"
                            src={URL.createObjectURL(file)}
                            sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                          />
                        ) : (
                          <InsertDriveFile sx={{ fontSize: 28, color: '#94A3B8' }} />
                        )}
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                            {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => handleRemoveFile(index)} sx={{ color: '#94A3B8' }}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={1.5} mt={1}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/suporte')}
                  sx={{ color: '#64748B', borderColor: '#CBD5E1' }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Send />}
                  sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#334155' } }}
                >
                  {loading && files.length > 0 ? 'Enviando...' : 'Enviar Ticket'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

export default TicketCreate
